import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {useCallback, useEffect, useRef, useState} from 'react';
import {Button, Checkbox, Modal} from 'letrus-ui';
import styles from './FocusModeInstructionModal.module.scss';

interface FocusModeInstructionModalProps {
  id: string;
  onClose?(): void;
  onStart(): Promise<void> | void;
  loading?: boolean;
  error?: string;
}

type Step = 1 | 2;

function FocusModeInstructionModal({
  id,
  onClose,
  onStart,
  loading = false,
  error,
}: FocusModeInstructionModalProps): JSX.Element {
  const modalRef = useRef<HTMLDialogElement>(null);
  const [step, setStep] = useState<Step>(1);
  const [hasAcceptedRules, setHasAcceptedRules] = useState(false);

  useEffect(() => {
    const modal = modalRef.current;

    if (!modal) {
      return undefined;
    }

    function handleNativeClose() {
      setStep(1);
      setHasAcceptedRules(false);
      onClose?.();
    }

    modal.addEventListener('close', handleNativeClose);

    return () => {
      modal.removeEventListener('close', handleNativeClose);
    };
  }, [onClose]);

  const handleClose = useCallback(() => {
    if (loading) {
      return;
    }

    const modal = modalRef.current;

    if (modal?.open) {
      modal.close();
    }
  }, [loading]);

  const handleConfirmStart = useCallback(async () => {
    if (!hasAcceptedRules || loading) {
      return;
    }

    await onStart();
  }, [hasAcceptedRules, loading, onStart]);

  return (
    <Modal
      id={id}
      ref={modalRef}
      className={styles.modal}
      childClassName={styles.content}
      closeButton={false}
    >
      <section className={styles.header}>
        <div className={styles.headerIcon}>
          <FontAwesomeIcon
            icon={step === 1 ? 'triangle-exclamation' : 'lock'}
          />
        </div>

        <h2>{step === 1 ? 'Como a atividade funciona' : 'Modo Foco ativo'}</h2>

        <p>
          {step === 1
            ? 'Entenda as regras de tempo antes de começar.'
            : 'Restrições ativas durante toda a atividade.'}
        </p>

        <div className={styles.stepper} aria-hidden>
          {[1, 2].map((stepperStep) => (
            <span
              key={stepperStep}
              className={`${styles.stepDot} ${
                step === stepperStep ? styles.stepDotActive : ''
              }`}
            />
          ))}
        </div>
      </section>

      {step === 1 ? (
        <section className={styles.body}>
          <ul>
            <li>
              <FontAwesomeIcon
                icon={['far', 'clock']}
                className={styles.listIcon}
              />
              <span>
                A trilha tem um tempo de duração, que será indicado pelo timer
                na parte superior da tela.
              </span>
            </li>
            <li>
              <FontAwesomeIcon
                icon={['far', 'clock']}
                className={styles.listIcon}
              />
              <span>
                Ao final do tempo, a atividade é encerrada automaticamente.
              </span>
            </li>
            <li>
              <FontAwesomeIcon
                icon={['far', 'clock']}
                className={styles.listIcon}
              />
              <span>Após o início, não é possível pausar.</span>
            </li>
          </ul>
        </section>
      ) : (
        <section className={styles.body}>
          <ul>
            <li>
              <FontAwesomeIcon
                icon={['far', 'display-slash']}
                className={styles.listIcon}
              />
              <span>
                Não é permitido abrir novas abas ou sites externos. O uso de
                recursos externos é proibido durante toda a atividade.
              </span>
            </li>
            <li>
              <FontAwesomeIcon
                icon={['far', 'display-slash']}
                className={styles.listIcon}
              />
              <span>Saídas da tela da atividade são monitoradas.</span>
            </li>
            <li>
              <FontAwesomeIcon
                icon={['far', 'copy']}
                className={styles.listIcon}
              />
              <span>
                Não é permitido copiar os conteúdos dos questionários e
                proposta, nem colar conteúdos externos.
              </span>
            </li>
          </ul>

          <div className={styles.checkboxRow}>
            <Checkbox
              id="focus-mode-rules-acceptance"
              checked={hasAcceptedRules}
              onChange={(event) => setHasAcceptedRules(event.target.checked)}
              label="Li e compreendi todas as instruções."
            />
          </div>
        </section>
      )}

      <footer className={styles.actions}>
        {error && (
          <p className={styles.errorInline}>
            <FontAwesomeIcon icon="triangle-exclamation" />
            {error}
          </p>
        )}

        {step === 1 ? (
          <>
            <Button variant="secondary" type="button" onClick={handleClose}>
              Voltar
            </Button>

            <Button
              variant="primary"
              type="button"
              onClick={() => setStep(2)}
              disabled={loading}
            >
              Próximo
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="secondary"
              type="button"
              onClick={() => setStep(1)}
              disabled={loading}
            >
              Anterior
            </Button>

            <Button
              variant="primary"
              type="button"
              disabled={!hasAcceptedRules || loading}
              loading={loading}
              onClick={handleConfirmStart}
            >
              Começar atividade
            </Button>
          </>
        )}
      </footer>
    </Modal>
  );
}

export default FocusModeInstructionModal;
